import React from 'react'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'

import LayoutServices from 'components/LayoutServices'
import ServicesSection from 'components/Services/Section'
import BrandingHero from 'components/Services/Branding/Hero'
import BrandingParallax from 'components/Services/Branding/Parallax'
import StickyBrandPackage from 'components/Services/Branding/StickyBrandPackage'
import ServiceAnimatedAsset from 'components/Services/AnimatedAsset'

import GridAnimationOne from 'theme/animations/branding/grid-1.json'
import GridAnimationTwo from 'theme/animations/branding/grid-2.json'
import GridAnimationThree from 'theme/animations/branding/grid-3.json'
import ResearchAnimation from 'theme/animations/branding/research.json'
import LogosAnimation from 'theme/animations/branding/logos.json'
import RefinementAnimation from 'theme/animations/branding/refinement.json'
import DeliveryAnimation from 'theme/animations/branding/delivery.json'
import ToneAnimation from 'theme/animations/branding/tone.json'

import 'styles/pages/services-branding.scss'

function BrandingService() {
  const { m: isMobile } = useBreakpoint()

  const scrollThreshold = isMobile ? 0.1 : 0.25

  return (
    <LayoutServices
      metaTitle="Creative Branding Agency | Build with Studio"
      metaDescription="Strong branding is a necessity in today’s image focused world. Elevate your brand identity by partnering with our creative branding agency."
      metaUrl="https://buildwithstudio.com/services/creative-branding-agency/"
    >
      <BrandingHero />
      <ServicesSection isFullHeight>
        <div className="branding-section__title-one">
          <h2 className="branding-section__title --large">
            Why We <span className="studio-purple">Love</span>
          </h2>
          <h2 className="branding-section__title --small">Creating Brands</h2>
        </div>
      </ServicesSection>
      <ServicesSection isFullHeight customThreshold={scrollThreshold}>
        <div className="branding-section__two-columns">
          <div className="branding-section__column --first">
            <p className="branding-section__column-paragraph">
              <span>Our team has over</span>
              <span>20</span>
              <span>Years</span>
              <span>
                of branding experience for <br />
                startups of all sizes
              </span>
            </p>
          </div>
          <div className="branding-section__column --second">
            <p className="branding-section__column-paragraph">
              Great branding is the foundation of a great digital product.
              Household brands like Apple and Google have set digital product
              users&apos; expectations high. Because of this, a great brand is
              no longer a luxury, it&apos;s a requirement.
            </p>
            <p className="branding-section__column-paragraph">
              From startup branding to large enterprises, we work closely with
              our clients to establish the brand tone and visual language needed
              to build a great brand. Then, we integrate this into our product
              design work to bring everything to life.
            </p>
          </div>
        </div>
      </ServicesSection>
      {/* <ServicesSection
        className="--branding-showreel"
        hasExtraPadding
        customThreshold={scrollThreshold}
      >
        <div className="branding-section__video-player">Showreel</div>
      </ServicesSection> */}
      <ServicesSection hasNoPadding customThreshold={scrollThreshold}>
        <div className="branding-section__title-two">
          <h2 className="branding-section__title --small">How Can Branding</h2>
          <h2 className="branding-section__title --large">
            <span className="studio-purple">Elevate</span>
          </h2>
          <h2 className="branding-section__title --small">your product</h2>
        </div>
      </ServicesSection>
      <ServicesSection hasNoPadding customThreshold={scrollThreshold}>
        <div className="branding-section__parallax">
          <BrandingParallax />
        </div>
      </ServicesSection>
      <ServicesSection customThreshold={isMobile ? 0.1 : null}>
        <p className="branding-section__quote --quote-one">
          “A brand is the foundation of a company&apos;s tone and ethos.”
        </p>
        <p className="branding-section__quote-paragraph">
          Today&apos;s world is becoming increasingly attuned to not just what
          is said, but how it&apos;s said; in addition, ethos, purpose and
          mission-driven companies are winning the hearts and minds of
          consumers.
        </p>
        <p className="branding-section__quote --quote-two">
          A great brand ensures your product will resonate with your target
          audience.
        </p>
        <div className="branding-section__quote-columns">
          <p className="branding-section__quote-paragraph">
            The way a brand presents itself through its logo, color scheme,
            messaging, and overall aesthetic can influence how people feel about
            it and whether they are attracted to it (or repelled by it).
          </p>
          <p className="branding-section__quote-paragraph">
            For example, a startup brand with bright, bold colors and a playful
            logo, while a brand with more muted tones and a minimalist design
            may appeal to a more mature and sophisticated audience.
          </p>
        </div>
      </ServicesSection>
      <ServicesSection customThreshold={scrollThreshold}>
        <div className="branding-section__title-three">
          <h2 className="branding-section__title --large">
            A <span className="studio-purple">Great</span> branding
          </h2>
          <h2 className="branding-section__title --medium">
            agency for startups
          </h2>
          <h2 className="branding-section__title --small">
            helps in the following ways
          </h2>
        </div>
      </ServicesSection>
      <ServicesSection customThreshold={scrollThreshold} hasExtraPadding>
        <div className="branding-section__grid">
          <div className="branding-section__grid-item --1x2">
            <div className="branding__animation-container --has-background">
              <ServiceAnimatedAsset src={GridAnimationOne} />
            </div>
            <p className="branding-section__paragraph">
              Creates a unique identity that differentiates your products or
              services from those of competitors.
            </p>
          </div>
          <div className="branding-section__grid-item --1x1">
            <div className="branding__animation-container --has-background">
              <ServiceAnimatedAsset src={GridAnimationTwo} />
            </div>
            <p className="branding-section__paragraph">
              Builds customer loyalty, increases brand recognition, and creates
              a positive reputation in the market.
            </p>
          </div>
          <div className="branding-section__grid-item --1x1">
            <div className="branding__animation-container --has-background">
              <ServiceAnimatedAsset src={GridAnimationThree} />
            </div>
            <p className="branding-section__paragraph">
              Attracts new customers, and makes it easier to introduce new
              products or services.
            </p>
          </div>
        </div>
      </ServicesSection>
      <ServicesSection isFullHeight customThreshold={scrollThreshold}>
        <div className="branding-section__title-four">
          <h2 className="branding-section__title --large">Our Process</h2>
        </div>
        <p className="branding-section__paragraph --narrow --has-border">
          Our startup branding efforts can be stand-alone but often run
          concurrently with our Strategy & Design efforts.
        </p>
        <p className="branding-section__paragraph --narrow --has-border">
          When we&apos;re designing and developing a product, we use a set of
          libraries that are defined by the brand. A design system and component
          library are important resources we need to ensure a consistent and
          thoughtful product.
        </p>
        <p className="branding-section__paragraph --narrow">
          We&apos;ve outlined our typical branding process below:
        </p>
      </ServicesSection>
      <ServicesSection customThreshold={scrollThreshold} hasExtraPadding>
        <div className="branding-section__figure">
          <h2 className="branding-section__title --small --left">
            Research & Moodboarding
          </h2>
          <p className="branding-section__paragraph">
            Moodboarding is an essential step when beginning a startup branding
            exercise. It helps us group styles that we think will resonate with
            users and their needs. We look at what&apos;s out in the market
            today and compare visual examples of our findings. This is a good
            way to show the client what&apos;s already on the market.
          </p>
          <div className="branding__animation-container">
            <ServiceAnimatedAsset
              src={ResearchAnimation}
              customThreshold={0.2}
            />
          </div>
        </div>
      </ServicesSection>
      <ServicesSection customThreshold={scrollThreshold} hasExtraPadding>
        <div className="branding-section__figure">
          <h2 className="branding-section__title --small --left">brand tone</h2>
          <p className="branding-section__paragraph">
            Establishing the brand tone is the pairing of brand voice with brand
            values. This sets the foundational principles of the logo
            directions. It also helps identify other elements like imagery and
            social media assets.
          </p>
          <div className="branding__animation-container --has-background --no-border">
            <ServiceAnimatedAsset src={ToneAnimation} customThreshold={0.2} />
          </div>
        </div>
      </ServicesSection>
      <ServicesSection customThreshold={scrollThreshold} hasExtraPadding>
        <div className="branding-section__figure">
          <h2 className="branding-section__title --small --left">
            logo direction
          </h2>
          <p className="branding-section__paragraph">
            After the exploration phase, we will create 3 to 4 logo directions.
            We love this part of the startup branding process since it&apos;s
            our opportunity to flex some creative muscle. In most cases, there
            will be a clear winner, but in other cases, we will pick elements we
            like from multiple directions and create a hybrid.
          </p>
          <div className="branding__animation-container --has-background --no-border">
            <ServiceAnimatedAsset src={LogosAnimation} customThreshold={0.2} />
          </div>
        </div>
      </ServicesSection>
      <ServicesSection customThreshold={scrollThreshold} hasExtraPadding>
        <div className="branding-section__figure">
          <h2 className="branding-section__title --small --left">
            refinement & applications
          </h2>
          <p className="branding-section__paragraph">
            Once a logo direction has been chosen, we will go through a
            refinement phase, where we iron out the imperfections and get it
            ready to be sent out into the world. We will also make sure that the
            logo translates well over multiple formats from large billboards to
            small email signatures. We want your startup brand to stand out.
          </p>
          <p className="branding-section__paragraph">
            When we&apos;re developing a product, we use a set of libraries that
            are defined by the brand. A design system and component library are
            important resources we need to ensure a consistent and thoughtful
            product
          </p>
          <div className="branding__animation-container">
            <ServiceAnimatedAsset
              src={RefinementAnimation}
              customThreshold={0.2}
            />
          </div>
        </div>
      </ServicesSection>
      <ServicesSection customThreshold={scrollThreshold} hasExtraPadding>
        <div className="branding-section__figure">
          <h2 className="branding-section__title --small --left">
            Delivery & marketing
          </h2>
          <p className="branding-section__paragraph">
            Once everything is approved, we will provide a brand package (see
            below) that contains multiple elements. If we&apos;re also building
            a product for you, then we will then apply the brand throughout our
            design sprints. We&apos;re not just your startup branding agency
            partner for the short-term; we also offer continued support if you
            require marketing asset
          </p>
          <div className="branding__animation-container">
            <ServiceAnimatedAsset
              src={DeliveryAnimation}
              customThreshold={0.2}
            />
          </div>
        </div>
      </ServicesSection>
      <ServicesSection customThreshold={scrollThreshold} hasNoPadding>
        <div className="branding-section__title-five">
          {isMobile ? (
            <h2 className="branding-section__title --small">
              What You receive in{' '}
              <span className="studio-purple">a brand package</span>
            </h2>
          ) : (
            <>
              <h2 className="branding-section__title --small">
                What You receive in
              </h2>
              <h2 className="branding-section__title --large">
                <span className="studio-purple">a brand package</span>
              </h2>
            </>
          )}
        </div>
      </ServicesSection>
      <section className="services__section --branding-package --centered --white">
        <StickyBrandPackage />
      </section>
    </LayoutServices>
  )
}

export default BrandingService
